import { sectionRenderer } from '@theme/section-renderer';

if (!customElements.get('category-chips-component')) {
  const WRAPPER_CLASS = 'sub-category-cards-wrapper';
  const COLLAPSED_CLASS = 'sub-category-cards-wrapper--collapsed';

  class CategoryChipsComponent extends HTMLElement {
    static observedAttributes = ['class'];

    /**
     * Last known collapsed state of the sub-category cards wrapper. Undefined
     * until connected so the initial attribute pass doesn't animate.
     * @type {boolean | undefined}
     */
    #collapsed;

    connectedCallback() {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('pointerover', this.#handlePointerOver);
      this.#collapsed = this.#isCollapsed();
      this.#applyDefaultChip();
    }

    /**
     * In hidden-"All" mode a clean arrival presents the first chip as selected
     * without its filter applied, so the grid and sub-category cards reflect
     * the whole collection. Liquid exposes that chip's URL as default-chip-url;
     * apply it as soon as the facets form is ready, replacing the history entry
     * so Back never returns to the unfiltered state. Skipped once the user has
     * clicked a chip themselves.
     */
    async #applyDefaultChip() {
      const url = this.getAttribute('default-chip-url');
      if (!url) return;

      // Built for a page that arrived with a legacy category value (the URL
      // keeps it): category-filter-upgrade.js moves that onto the reference
      // filter, and applying this default too would override it. Checked on
      // the URL itself rather than the location, which the upgrade may
      // already have replaced by now.
      if (url.includes('filter.p.m.custom.category=')) return;

      // One shot per page load: a section morph re-adds the attribute on a
      // cleared filter state, but only a fresh connection applies it.
      this.removeAttribute('default-chip-url');

      await customElements.whenDefined('facets-form-component');
      if (!this.isConnected || CategoryChipsComponent.#clickToken) return;

      const facetsForm = this.#findFacetsForm();
      if (!facetsForm || typeof facetsForm.updateFiltersByURL !== 'function') return;

      facetsForm.updateFiltersByURL(url, { replaceHistory: true });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#handleClick);
      this.removeEventListener('pointerover', this.#handlePointerOver);
    }

    /**
     * The section morph flips the collapsed modifier while the main thread is
     * still busy applying the rest of the new section (a big product grid
     * diff). A transition starting at that moment plays out during the blocked
     * frames and shows as a 2-frame jump. So: freeze the wrapper at its
     * previous visual state inline (applied before the next paint), then
     * release it two frames later, when the morph work is done — the height
     * then animates on an idle main thread.
     * @param {string} name
     * @param {string | null} oldValue
     * @param {string | null} newValue
     */
    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'class' || oldValue === newValue) return;
      if (!this.classList.contains(WRAPPER_CLASS)) return;

      const collapsed = this.#isCollapsed();
      if (this.#collapsed === undefined || collapsed === this.#collapsed) {
        this.#collapsed = collapsed;
        return;
      }
      this.#collapsed = collapsed;

      // Deferred to a microtask: this callback fires mid-morph, and the morph's
      // attribute copying would wipe an inline style set here (the new server
      // HTML carries no style attribute). The microtask runs after the whole
      // morph task, still before the next paint.
      queueMicrotask(() => {
        if (this.#isCollapsed() !== collapsed) return;

        this.style.transition = 'none';
        this.style.gridTemplateRows = collapsed ? '1fr' : '0fr';

        this.#releaseWhenCalm();
      });
    }

    /**
     * Clears the inline freeze so the CSS height transition starts — but only
     * once the view transition (the product-card cross-fade) has finished and
     * frames are flowing at a normal cadence again. Right after the morph the
     * main thread is still busy (image decodes, component updates); a height
     * transition started there loses most of its frames and reads as a jump,
     * and one started mid view transition would fight its snapshots.
     * @param {number} [prevFrameTime]
     * @param {number} [tries]
     */
    #releaseWhenCalm(prevFrameTime = performance.now(), tries = 0) {
      requestAnimationFrame(() => {
        const now = performance.now();
        const frameGap = now - prevFrameTime;
        const viewTransitionActive = document.documentElement.hasAttribute('data-active-view-transition');

        if ((viewTransitionActive || frameGap > 25) && tries < 60) {
          this.#releaseWhenCalm(now, tries + 1);
          return;
        }

        this.style.transition = '';
        this.style.gridTemplateRows = '';
      });
    }

    #isCollapsed() {
      return this.classList.contains(COLLAPSED_CLASS);
    }

    /**
     * Cancels a deferred filter update from an earlier closing click.
     * @type {(() => void) | null}
     */
    static #cancelPendingUpdate = null;

    /**
     * Identifies the most recent chip click, so async continuations from an
     * earlier click (e.g. a section HTML peek) can detect they're stale.
     * @type {symbol | null}
     */
    static #clickToken = null;

    #handleClick = (event) => {
      const chip = event.target.closest('[data-chip-url]');
      if (!chip || !this.contains(chip)) return;

      const url = chip.dataset.chipUrl;
      if (!url) return;

      event.preventDefault();

      const facetsForm = this.#findFacetsForm();
      if (!facetsForm || typeof facetsForm.updateFiltersByURL !== 'function') return;

      CategoryChipsComponent.#cancelPendingUpdate?.();
      const token = Symbol('chip-click');
      CategoryChipsComponent.#clickToken = token;

      // The chip URL is already built for single-select ("tab") behavior in
      // Liquid: activating a chip drops every other value of the same filter so
      // only one stays selected. We just apply it via the native AJAX flow,
      // including the theme's product-grid view transition (the card
      // cross-fade). The row itself stays frozen at its previous height until
      // that transition ends (see #releaseWhenCalm), so nothing shifts
      // mid-capture and the two animations run in sequence, never against
      // each other.
      const applyUpdate = () => facetsForm.updateFiltersByURL(url);

      // Sub-category card clicks never close the row; only chips-bar clicks
      // can, and only when the row is currently open.
      const wrapper = this.classList.contains('category-chips-wrapper')
        ? this.closest('.shopify-section')?.querySelector(`category-chips-component.${WRAPPER_CLASS}`)
        : null;

      if (!(wrapper instanceof CategoryChipsComponent) || wrapper.#isCollapsed()) {
        applyUpdate();
        return;
      }

      // The "All" chip and deselecting the active chip are guaranteed to close
      // the row — start closing right away with the current cards.
      if (chip.hasAttribute('data-chip-all') || chip.getAttribute('aria-pressed') === 'true') {
        this.#closeThenApply(wrapper, url, applyUpdate);
        return;
      }

      // Switching to another category: whether the row stays open depends on
      // that category's sub-categories, which only the target section HTML
      // knows. Peek at it (usually already prefetched on hover — and the
      // renderer reuses this cache, so no extra request) and close first when
      // the target has no cards; otherwise the row keeps its height and only
      // the content morphs.
      const sectionId = this.getAttribute('section-id');
      if (!sectionId) {
        applyUpdate();
        return;
      }

      sectionRenderer
        .getSectionHTML(sectionId, true, new URL(url, location.origin))
        .then((html) => {
          if (CategoryChipsComponent.#clickToken !== token) return;

          const keepsRowOpen = html.includes(WRAPPER_CLASS) && !html.includes(COLLAPSED_CLASS);
          if (keepsRowOpen) {
            applyUpdate();
          } else {
            this.#closeThenApply(wrapper, url, applyUpdate);
          }
        })
        .catch(() => {
          // Never dead-end the click on a failed peek.
          if (CategoryChipsComponent.#clickToken === token) applyUpdate();
        });
    };

    /**
     * Animates the row closed with its current cards, then applies the filter
     * update. Applying it right away would run the section morph's main-thread
     * work mid-animation; deferring it also hides the fetch/morph latency
     * behind the motion. The cache is warmed during the animation.
     * @param {CategoryChipsComponent} wrapper
     * @param {string} url
     * @param {() => void} applyUpdate
     */
    #closeThenApply(wrapper, url, applyUpdate) {
      wrapper.#collapsed = true;
      wrapper.classList.add(COLLAPSED_CLASS);
      wrapper.querySelector('.sub-category-cards__viewport')?.setAttribute('inert', '');

      const sectionId = this.getAttribute('section-id');
      if (sectionId) {
        sectionRenderer.getSectionHTML(sectionId, true, new URL(url, location.origin)).catch(() => {});
      }

      let applied = false;
      const applyOnce = () => {
        if (applied) return;
        applied = true;
        CategoryChipsComponent.#cancelPendingUpdate = null;
        wrapper.removeEventListener('transitionend', onTransitionEnd);
        clearTimeout(fallbackTimer);
        applyUpdate();
      };
      // Only the wrapper's own height transition counts: transitionend bubbles,
      // and e.g. a card's hover-border transition ending mid-close would apply
      // the update too early, blocking the animation it was deferred around.
      const onTransitionEnd = (event) => {
        if (event.target !== wrapper || event.propertyName !== 'grid-template-rows') return;
        applyOnce();
      };
      const fallbackTimer = setTimeout(applyOnce, 450);
      wrapper.addEventListener('transitionend', onTransitionEnd);

      CategoryChipsComponent.#cancelPendingUpdate = () => {
        applied = true;
        CategoryChipsComponent.#cancelPendingUpdate = null;
        wrapper.removeEventListener('transitionend', onTransitionEnd);
        clearTimeout(fallbackTimer);
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) applyOnce();
    }

    /**
     * Warm the section cache while the pointer is over a chip, so the section
     * HTML is (usually) already local by the time the user clicks and the row
     * can react without a network wait.
     * @param {Event} event
     */
    #handlePointerOver = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const chip = target.closest('[data-chip-url]');
      if (!chip || !this.contains(chip)) return;

      const url = chip.getAttribute('data-chip-url');
      const sectionId = this.getAttribute('section-id');
      if (!url || !sectionId) return;

      sectionRenderer.getSectionHTML(sectionId, true, new URL(url, location.origin)).catch(() => {});
    };

    #findFacetsForm() {
      const sectionId = this.getAttribute('section-id');
      if (!sectionId) return null;

      const candidates = document.querySelectorAll(
        `facets-form-component[section-id="${CSS.escape(sectionId)}"]`
      );

      for (const candidate of candidates) {
        if (!candidate.closest('dialog')) return candidate;
      }

      return candidates[0] ?? null;
    }
  }

  customElements.define('category-chips-component', CategoryChipsComponent);
}
